import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import WeddingHallNav from '../components/WeddingHallNav';
import { dayManagementApi } from '../api';

// 타임라인 상태 색상
const TIMELINE_STATUS_COLORS: Record<string, string> = {
  '대기': 'bg-gray-100 text-gray-700 border-gray-300',
  '진행중': 'bg-blue-100 text-blue-700 border-blue-400',
  '완료': 'bg-green-100 text-green-700 border-green-400',
  '건너뜀': 'bg-orange-100 text-orange-700 border-orange-300',
};

// 체크리스트 카테고리 색상
const CHECKLIST_CAT_COLORS: Record<string, string> = {
  '사전준비': 'bg-purple-500',
  '당일준비': 'bg-blue-500',
  '예식중': 'bg-pink-500',
  '예식후': 'bg-teal-500',
};

const TIMELINE_STATUSES = ['대기', '진행중', '완료', '건너뜀'];
const CHECKLIST_CATEGORIES = ['사전준비', '당일준비', '예식중', '예식후'];

interface TimelineItem {
  whTimelineIdx: number;
  timelineOrder: number;
  timeLabel: string;
  taskName: string;
  status: string;
  actualTime: string | null;
  memo: string | null;
  assignedStaff?: { name: string };
}

interface ChecklistGroup {
  category: string;
  items: ChecklistItem[];
  total: number;
  completed: number;
  progress: number;
}

interface ChecklistItem {
  whChecklistIdx: number;
  checklistCategory: string;
  taskName: string;
  isCompleted: string;
  dueTime: string | null;
  completedDt: string | null;
  memo: string | null;
  sortOrder: number;
  assignedStaff?: { name: string };
}

interface Customer {
  whCustomerIdx: number;
  groomName: string;
  brideName: string;
  contactName: string;
  eventType: string;
  eventDt: string;
  eventTime: string;
  weddingVenue?: { venueName: string };
}

export default function DayManagement() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const customerId = Number(searchParams.get('id'));

  const [activeTab, setActiveTab] = useState<'timeline' | 'checklist'>('timeline');
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [timelineItems, setTimelineItems] = useState<TimelineItem[]>([]);
  const [checklistGroups, setChecklistGroups] = useState<ChecklistGroup[]>([]);
  const [loading, setLoading] = useState(false);

  // 타임라인 항목 추가 모달
  const [showTimelineModal, setShowTimelineModal] = useState(false);
  const [newTimeline, setNewTimeline] = useState({ timeLabel: '', taskName: '', memo: '' });

  // 체크리스트 항목 추가 모달
  const [showChecklistModal, setShowChecklistModal] = useState(false);
  const [newChecklist, setNewChecklist] = useState({ checklistCategory: '사전준비', taskName: '', dueTime: '', memo: '' });

  const fetchTimeline = useCallback(async () => {
    if (!customerId) return;
    try {
      const data = await dayManagementApi.getTimeline(customerId);
      setCustomer(data.customer);
      setTimelineItems(data.items || []);
    } catch (error) {
      console.error('진행표 조회 실패:', error);
    }
  }, [customerId]);

  const fetchChecklist = useCallback(async () => {
    if (!customerId) return;
    try {
      const data = await dayManagementApi.getChecklist(customerId);
      setCustomer(data.customer);
      setChecklistGroups(data.grouped || []);
    } catch (error) {
      console.error('체크리스트 조회 실패:', error);
    }
  }, [customerId]);

  useEffect(() => {
    if (!customerId) return;
    setLoading(true);
    Promise.all([fetchTimeline(), fetchChecklist()]).finally(() => setLoading(false));
  }, [customerId, fetchTimeline, fetchChecklist]);

  // 타임라인 상태 변경
  const updateTimelineStatus = async (item: TimelineItem, newStatus: string) => {
    try {
      await dayManagementApi.updateTimeline(item.whTimelineIdx, { status: newStatus });
      fetchTimeline();
    } catch (error) {
      console.error('상태 변경 실패:', error);
    }
  };

  // 타임라인 삭제
  const deleteTimelineItem = async (id: number) => {
    if (!confirm('삭제하시겠습니까?')) return;
    try {
      await dayManagementApi.deleteTimeline(id);
      fetchTimeline();
    } catch (error) {
      console.error('삭제 실패:', error);
    }
  };

  // 타임라인 추가
  const addTimelineItem = async () => {
    if (!newTimeline.taskName) return alert('항목명은 필수입니다.');
    try {
      await dayManagementApi.addTimeline(customerId, {
        ...newTimeline,
        timelineOrder: timelineItems.length + 1,
      });
      setShowTimelineModal(false);
      setNewTimeline({ timeLabel: '', taskName: '', memo: '' });
      fetchTimeline();
    } catch (error: any) {
      alert(`오류: ${error.message}`);
    }
  };

  // 타임라인 템플릿 적용
  const applyTimelineTemplate = async () => {
    if (timelineItems.length > 0 && !confirm('기존 항목에 추가됩니다. 계속하시겠습니까?')) return;
    try {
      await dayManagementApi.applyTimelineTemplate(customerId);
      fetchTimeline();
    } catch (error: any) {
      alert(`오류: ${error.message}`);
    }
  };

  // 체크리스트 토글
  const toggleChecklist = async (item: ChecklistItem) => {
    const newCompleted = item.isCompleted === 'y' ? 'n' : 'y';
    try {
      await dayManagementApi.updateChecklist(item.whChecklistIdx, { isCompleted: newCompleted });
      fetchChecklist();
    } catch (error) {
      console.error('체크 변경 실패:', error);
    }
  };

  // 체크리스트 삭제
  const deleteChecklistItem = async (id: number) => {
    if (!confirm('삭제하시겠습니까?')) return;
    try {
      await dayManagementApi.deleteChecklist(id);
      fetchChecklist();
    } catch (error) {
      console.error('삭제 실패:', error);
    }
  };

  // 체크리스트 추가
  const addChecklistItem = async () => {
    if (!newChecklist.taskName) return alert('항목명은 필수입니다.');
    try {
      await dayManagementApi.addChecklist(customerId, newChecklist);
      setShowChecklistModal(false);
      setNewChecklist({ checklistCategory: '사전준비', taskName: '', dueTime: '', memo: '' });
      fetchChecklist();
    } catch (error: any) {
      alert(`오류: ${error.message}`);
    }
  };

  // 체크리스트 템플릿 적용
  const applyChecklistTemplate = async () => {
    if (checklistGroups.some((g) => g.items.length > 0) && !confirm('기존 항목에 추가됩니다. 계속하시겠습니까?')) return;
    try {
      await dayManagementApi.applyChecklistTemplate(customerId);
      fetchChecklist();
    } catch (error: any) {
      alert(`오류: ${error.message}`);
    }
  };

  if (!customerId) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 overflow-auto">
          <div className="p-8">
            <WeddingHallNav />
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <i className="ri-file-list-3-line text-5xl text-gray-300 mb-4 block"></i>
              <p className="text-gray-500 mb-4">고객을 선택해주세요.</p>
              <button onClick={() => navigate('/wedding-hall/customers')} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                고객목록으로 이동
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const customerLabel = customer
    ? customer.eventType === '예식'
      ? [customer.groomName, customer.brideName].filter(Boolean).join(' & ')
      : customer.contactName || '-'
    : '';

  const totalChecklist = checklistGroups.reduce((s, g) => s + g.total, 0);
  const totalCompleted = checklistGroups.reduce((s, g) => s + g.completed, 0);
  const overallProgress = totalChecklist > 0 ? Math.round((totalCompleted / totalChecklist) * 100) : 0;

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <div className="p-8">
          <WeddingHallNav />

          {/* 고객 헤더 */}
          {customer && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center">
                    <i className="ri-heart-line text-xl text-pink-500"></i>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-800">{customerLabel}</h2>
                    <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                      <span><i className="ri-calendar-line mr-1"></i>{customer.eventDt || '미정'}</span>
                      <span><i className="ri-time-line mr-1"></i>{customer.eventTime?.substring(0, 5) || '미정'}</span>
                      <span><i className="ri-building-4-line mr-1"></i>{customer.weddingVenue?.venueName || '미정'}</span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => navigate(`/wedding-hall/customer-detail?id=${customerId}`)}
                  className="px-3 py-1.5 text-sm text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100"
                >
                  고객상세 보기
                </button>
              </div>
            </div>
          )}

          {/* 탭 */}
          <div className="flex gap-2 mb-6 border-b border-gray-200">
            <button
              onClick={() => setActiveTab('timeline')}
              className={`px-6 py-3 font-medium transition-colors ${
                activeTab === 'timeline'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <i className="ri-timer-line mr-1.5"></i>당일 진행표
            </button>
            <button
              onClick={() => setActiveTab('checklist')}
              className={`px-6 py-3 font-medium transition-colors ${
                activeTab === 'checklist'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <i className="ri-checkbox-multiple-line mr-1.5"></i>체크리스트
              {totalChecklist > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">
                  {totalCompleted}/{totalChecklist}
                </span>
              )}
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              {/* ========== 타임라인 탭 ========== */}
              {activeTab === 'timeline' && (
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-bold text-gray-800">예식 당일 진행표</h3>
                    <div className="flex gap-2">
                      <button
                        onClick={applyTimelineTemplate}
                        className="px-4 py-2 text-sm bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 flex items-center gap-1"
                      >
                        <i className="ri-magic-line"></i> 템플릿 적용
                      </button>
                      <button
                        onClick={() => setShowTimelineModal(true)}
                        className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-1"
                      >
                        <i className="ri-add-line"></i> 항목 추가
                      </button>
                    </div>
                  </div>

                  {timelineItems.length === 0 ? (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                      <i className="ri-timer-line text-5xl text-gray-300 mb-4 block"></i>
                      <p className="text-gray-500 mb-4">진행표가 없습니다. 템플릿을 적용하거나 직접 추가하세요.</p>
                    </div>
                  ) : (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                      <div className="divide-y divide-gray-100">
                        {timelineItems.map((item, idx) => {
                          const statusColor = TIMELINE_STATUS_COLORS[item.status] || TIMELINE_STATUS_COLORS['대기'];
                          return (
                            <div
                              key={item.whTimelineIdx}
                              className="flex items-center gap-4 px-5 py-3 hover:bg-gray-50 transition-colors"
                            >
                              {/* 순서 */}
                              <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-sm font-bold text-gray-500">
                                {idx + 1}
                              </div>

                              {/* 시간 */}
                              <div className="w-16 text-sm font-mono font-semibold text-gray-700">
                                {item.timeLabel || '-'}
                              </div>

                              {/* 내용 */}
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm font-medium ${item.status === '완료' ? 'text-gray-400 line-through' : 'text-gray-800'}`}>
                                  {item.taskName}
                                </p>
                                {item.memo && (
                                  <p className="text-xs text-gray-400 mt-0.5 truncate">{item.memo}</p>
                                )}
                              </div>

                              {/* 담당자 */}
                              {item.assignedStaff?.name && (
                                <span className="text-xs text-gray-400">
                                  <i className="ri-user-line mr-0.5"></i>
                                  {item.assignedStaff.name}
                                </span>
                              )}

                              {/* 실제 시간 */}
                              {item.actualTime && (
                                <span className="text-xs text-blue-500 font-mono">
                                  {item.actualTime.substring(0, 5)}
                                </span>
                              )}

                              {/* 상태 */}
                              <select
                                value={item.status}
                                onChange={(e) => updateTimelineStatus(item, e.target.value)}
                                className={`px-2 py-1 text-xs rounded-lg border ${statusColor} cursor-pointer`}
                              >
                                {TIMELINE_STATUSES.map((s) => (
                                  <option key={s} value={s}>{s}</option>
                                ))}
                              </select>

                              {/* 삭제 */}
                              <button
                                onClick={() => deleteTimelineItem(item.whTimelineIdx)}
                                className="text-gray-300 hover:text-red-500 transition-colors"
                              >
                                <i className="ri-delete-bin-line"></i>
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ========== 체크리스트 탭 ========== */}
              {activeTab === 'checklist' && (
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center gap-4">
                      <h3 className="text-lg font-bold text-gray-800">준비 체크리스트</h3>
                      {totalChecklist > 0 && (
                        <div className="flex items-center gap-2">
                          <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-green-500 rounded-full transition-all duration-300"
                              style={{ width: `${overallProgress}%` }}
                            ></div>
                          </div>
                          <span className="text-sm font-medium text-gray-600">{overallProgress}%</span>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={applyChecklistTemplate}
                        className="px-4 py-2 text-sm bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 flex items-center gap-1"
                      >
                        <i className="ri-magic-line"></i> 템플릿 적용
                      </button>
                      <button
                        onClick={() => setShowChecklistModal(true)}
                        className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-1"
                      >
                        <i className="ri-add-line"></i> 항목 추가
                      </button>
                    </div>
                  </div>

                  {checklistGroups.every((g) => g.items.length === 0) ? (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                      <i className="ri-checkbox-multiple-line text-5xl text-gray-300 mb-4 block"></i>
                      <p className="text-gray-500 mb-4">체크리스트가 없습니다. 템플릿을 적용하거나 직접 추가하세요.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {checklistGroups.map((group) => {
                        if (group.items.length === 0) return null;
                        const catColor = CHECKLIST_CAT_COLORS[group.category] || 'bg-gray-500';
                        return (
                          <div key={group.category} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                            <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <span className={`w-3 h-3 rounded-full ${catColor}`}></span>
                                <h4 className="font-semibold text-gray-800">{group.category}</h4>
                                <span className="text-xs text-gray-400">{group.completed}/{group.total}</span>
                              </div>
                              <div className="w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full transition-all duration-300 ${catColor}`}
                                  style={{ width: `${group.progress}%` }}
                                ></div>
                              </div>
                            </div>
                            <div className="divide-y divide-gray-50">
                              {group.items.map((item) => (
                                <div
                                  key={item.whChecklistIdx}
                                  className="flex items-center gap-3 px-5 py-2.5 hover:bg-gray-50 transition-colors"
                                >
                                  <button
                                    onClick={() => toggleChecklist(item)}
                                    className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors cursor-pointer ${
                                      item.isCompleted === 'y'
                                        ? 'bg-green-500 border-green-500 text-white'
                                        : 'border-gray-300 hover:border-green-400'
                                    }`}
                                  >
                                    {item.isCompleted === 'y' && <i className="ri-check-line text-xs"></i>}
                                  </button>
                                  <span
                                    className={`flex-1 text-sm ${
                                      item.isCompleted === 'y' ? 'text-gray-400 line-through' : 'text-gray-800'
                                    }`}
                                  >
                                    {item.taskName}
                                  </span>
                                  {item.dueTime && (
                                    <span className="text-xs text-gray-400 font-mono">{item.dueTime.substring(0, 5)}</span>
                                  )}
                                  {item.assignedStaff?.name && (
                                    <span className="text-xs text-gray-400">{item.assignedStaff.name}</span>
                                  )}
                                  <button
                                    onClick={() => deleteChecklistItem(item.whChecklistIdx)}
                                    className="text-gray-300 hover:text-red-500 transition-colors"
                                  >
                                    <i className="ri-close-line"></i>
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* 타임라인 추가 모달 */}
      {showTimelineModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">진행표 항목 추가</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">시간 표시</label>
                <input type="text" placeholder="예: -30분, 본식, +10분" value={newTimeline.timeLabel}
                  onChange={(e) => setNewTimeline({ ...newTimeline, timeLabel: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">항목명 <span className="text-red-500">*</span></label>
                <input type="text" placeholder="예: 신부 대기실 준비" value={newTimeline.taskName}
                  onChange={(e) => setNewTimeline({ ...newTimeline, taskName: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">메모</label>
                <textarea rows={2} value={newTimeline.memo}
                  onChange={(e) => setNewTimeline({ ...newTimeline, memo: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowTimelineModal(false)} className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50">취소</button>
              <button onClick={addTimelineItem} className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700">추가</button>
            </div>
          </div>
        </div>
      )}

      {/* 체크리스트 추가 모달 */}
      {showChecklistModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">체크리스트 항목 추가</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">카테고리</label>
                <select value={newChecklist.checklistCategory}
                  onChange={(e) => setNewChecklist({ ...newChecklist, checklistCategory: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg">
                  {CHECKLIST_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">항목명 <span className="text-red-500">*</span></label>
                <input type="text" placeholder="예: 홀 테이블 세팅 확인" value={newChecklist.taskName}
                  onChange={(e) => setNewChecklist({ ...newChecklist, taskName: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">예정 시간</label>
                <input type="time" value={newChecklist.dueTime}
                  onChange={(e) => setNewChecklist({ ...newChecklist, dueTime: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">메모</label>
                <textarea rows={2} value={newChecklist.memo}
                  onChange={(e) => setNewChecklist({ ...newChecklist, memo: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowChecklistModal(false)} className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50">취소</button>
              <button onClick={addChecklistItem} className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700">추가</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
